import { config } from '../../../config/config';
import { EventName } from '../../eventing/interfaces/events.interface';
import type { IEvent } from '../../eventing/interfaces/events.interface';
import { loadScript } from '../../shared/utils/scripts';

declare global {
    interface Window {
        io_global_object_name: string;
        IGLOO: {
            getBlackbox: () => { blackbox: string };
            loader: {
                subkey: string;
                uri_hook: string;
                version: string;
                tp: boolean;
            };
        };
    }
}

class FraudForceService {
    private isInitialized = false;

    /**
     * Dispaches an event to the registered event dispatcher.
     */
    public eventDispatch: (event: IEvent) => boolean = () => false;

    /**
     * Registers an event dispatcher. Typically a component will call this using
     * the `useEventDispatcher` hook in order to forward events into the React
     * event bus. This avoids calling hooks from a non‑React class.
     */
    public registerEventDispatcher(dispatch: (event: IEvent) => boolean): void {
        this.eventDispatch = dispatch;
    }

    /**
     * Initializes the FraudForce script by configuring and adding it to the DOM.
     * This should be called once when the application loads.
     */
    initialize(): Promise<void> {
        if (this.isInitialized || !config.FRAUDFORCE.ENABLED) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            window.io_global_object_name = 'IGLOO';
            window.IGLOO = window.IGLOO || {};
            window.IGLOO.loader = {
                ...window.IGLOO.loader,
                subkey: config.FRAUDFORCE.SUBKEY,
                uri_hook: config.FRAUDFORCE.URI_HOOK,
                version: config.FRAUDFORCE.VERSION,
                tp: true, // Enable third-party cookie/script loading
            };

            loadScript(config.FRAUDFORCE.SCRIPT_URL)
                .then(() => {
                    this.isInitialized = true;
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    /**
     * Retrieves the FraudForce blackbox value.
     * It will retry up to 3 times with a 500ms delay if the blackbox is null or empty.
     * @returns A promise that resolves with the blackbox string.
     */
    async getBlackbox(): Promise<string> {
        const maxRetries = config.FRAUDFORCE.MAX_RETRIES ?? 3;
        const delay = config.FRAUDFORCE.RETRY_DELAY ?? 500;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            if (window.IGLOO && typeof window.IGLOO.getBlackbox === 'function') {
                const result = window.IGLOO.getBlackbox();
                const blackbox = result?.blackbox;

                if (this.isValidBlackbox(blackbox)) {
                    this.eventDispatch({ name: EventName.FRAUD_BLACKBOX_COLLECTED });
                    return blackbox;
                }
            }

            if (attempt < maxRetries) {
                await new Promise((resolve) => setTimeout(resolve, delay));
            }
        }

        console.log('[FraudForceService] Failed to get a valid blackbox after multiple attempts.');
        return '';
    }

    /**
     * Checks if a blackbox string is valid (not null, empty, or the string 'null').
     * @param blackbox The blackbox string to validate.
     * @returns True if the blackbox is valid, false otherwise.
     */
    isValidBlackbox(blackbox: string | null | undefined): boolean {
        return !!blackbox && blackbox !== 'null' && blackbox.length > 0;
    }
}

export const fraudForceService = new FraudForceService();
